if (!customElements.get('ceme-design-quiz')) {
  const PROGRESS = { 1: 0.12, 2: 0.55, 3: 0.55, 4: 0.55, 5: 0.55, 6: 0.55, 7: 0.33, 8: 0.33, 9: 0.66, 10: 0.06, 11: 0.36, 12: 0.68 };
  const NUMBER_RE = /^(\d+(\.\d+)?(\s+\d+\/\d+)?|\d+\/\d+)\s*("|”|''|in|inch|inches)?$/i;
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const ZIP_RE = /^[a-z0-9][a-z0-9 -]{1,8}[a-z0-9]$/i;
  const FILE_RE = /\.(jpe?g|png|pdf)$/i;
  const STATE_TTL = 30 * 24 * 60 * 60 * 1000;

  class CemeDesignQuiz extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-cdq-config]')?.textContent || '{}');
      } catch (error) {
        this.config = {};
      }
      this.form = this.querySelector('.cdq__form');
      this.thanks = this.querySelector('[data-cdq-thanks]');
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.bindDialogs();

      if (this.thanks && this.thanks.hasAttribute('data-cdq-done')) {
        this.clearState();
        this.classList.add('is-ready');
        this.afterSuccess();
        return;
      }
      if (!this.form) return;

      this.steps = Array.from(this.querySelectorAll('.cdq-step'));
      this.stepMap = new Map(this.steps.map((step) => [step.dataset.step, step]));
      this.prevBtn = this.querySelector('[data-cdq-prev]');
      this.nextBtn = this.querySelector('[data-cdq-next]');
      this.live = this.querySelector('[data-cdq-live]');
      this.progressItems = Array.from(this.querySelectorAll('.cdq-progress__item'));
      this.files = {};
      this.history = [];
      this.current = '1';

      this.bind();
      this.initUploads();

      let start = '1';
      const editorStep = this.designMode ? this.dataset.editorStep : '';
      if (editorStep === 'thanks') {
        this.classList.add('is-ready');
        this.showThanks(false);
        return;
      }
      if (editorStep) {
        start = editorStep;
        if (start !== '1') this.history = ['1'];
      } else if (!this.designMode) {
        const restored = this.restore();
        if (restored) {
          this.history = restored.history;
          start = restored.current;
        }
      }
      if (this.querySelector('[data-cdq-server-error]')) {
        if (!this.history.length) this.history = ['1'];
        start = '12';
      }

      this.classList.add('is-ready');
      this.show(start, { focus: false });
    }

    /* ---------- events ---------- */

    bind() {
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.prevBtn?.addEventListener('click', () => this.back());
      this.nextBtn?.addEventListener('click', () => this.next());
      this.form.addEventListener('submit', (event) => {
        if (this.allowSubmit) return;
        event.preventDefault();
        if (this.current === '12') this.submit();
        else this.next();
      });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.resetBusy();
      });
    }

    onClick(event) {
      const target = event.target;
      const tip = target.closest('[data-cdq-tip]');
      if (tip) {
        const open = tip.getAttribute('aria-expanded') !== 'true';
        this.closeTips();
        tip.setAttribute('aria-expanded', String(open));
        return;
      }
      this.closeTips();

      const radio = target.closest('.cdq-opt__input[type="radio"]');
      if (radio && !this.arrowNav) {
        this.scheduleAdvance(radio);
        return;
      }

      const cont = target.closest('[data-cdq-continue]');
      if (cont) {
        this.next();
        return;
      }

      const goto = target.closest('[data-cdq-goto]');
      if (goto) {
        if (goto.hasAttribute('data-cdq-clear-choice')) this.clearChoice(this.stepFor(goto));
        this.go(goto.dataset.cdqGoto);
        return;
      }

      if (target.closest('[data-cdq-submit]')) {
        this.submit();
        return;
      }

      const remove = target.closest('[data-cdq-file-remove]');
      if (remove) {
        const drop = remove.closest('[data-cdq-drop]');
        this.files[drop.dataset.key].splice(Number(remove.dataset.index), 1);
        this.renderFiles(drop);
        return;
      }

      const opener = target.closest('[data-cdq-dialog-open]');
      if (opener) this.openDialog(opener);
    }

    onChange(event) {
      const el = event.target;
      if (el.matches('.cdq-opt__input[type="radio"]')) {
        this.syncGroup(el);
        this.syncOther(this.stepFor(el));
      } else if (el.matches('[data-cdq-feature]')) {
        el.closest('.cdq-opt__label').classList.toggle('is-selected', el.checked);
        this.syncFeatures();
      } else if (el.matches('[data-cdq-consent]')) {
        this.syncConsent();
      }
      this.clearStepErrors(this.stepFor(el), el);
      this.save();
    }

    onInput(event) {
      const el = event.target;
      if (el.classList.contains('is-invalid')) {
        el.classList.remove('is-invalid');
        el.removeAttribute('aria-invalid');
        const err = this.errorFor(el);
        if (err && !this.stepFor(el).querySelector('.is-invalid[aria-describedby="' + el.getAttribute('aria-describedby') + '"]')) err.hidden = true;
      }
      this.save();
    }

    onKeydown(event) {
      const el = event.target;
      if (event.key === 'Escape') {
        this.closeTips();
        return;
      }
      if (el.matches && el.matches('input[type="radio"]') && event.key.startsWith('Arrow')) {
        this.arrowNav = true;
        setTimeout(() => { this.arrowNav = false; }, 0);
        return;
      }
      if (event.key !== 'Enter' || !el.matches || !el.matches('input') || el.type === 'file') return;
      event.preventDefault();
      if (this.current === '12') this.submit();
      else this.next();
    }

    /* ---------- navigation ---------- */

    stepFor(el) {
      return el.closest('.cdq-step');
    }

    go(target) {
      target = String(target);
      if (!this.stepMap.has(target) || target === this.current) return;
      this.history.push(this.current);
      this.show(target);
    }

    back() {
      if (!this.history.length) return;
      this.show(this.history.pop());
    }

    next() {
      const step = this.stepMap.get(this.current);
      if (!step || step.hasAttribute('data-final')) return;
      if (!this.validate(step)) return;
      const target = this.nextTarget(step);
      if (target) this.go(target);
    }

    nextTarget(step) {
      const checked = step.querySelector('.cdq-opt__input[type="radio"]:checked');
      if (checked) {
        if (checked.hasAttribute('data-other')) return step.querySelector('[data-cdq-other-continue] [data-next]')?.dataset.next;
        return checked.dataset.next;
      }
      const skip = step.querySelector('.cdq-skip [data-cdq-goto]');
      if (skip) return skip.dataset.cdqGoto;
      return step.querySelector('[data-cdq-continue]')?.dataset.next;
    }

    scheduleAdvance(radio) {
      if (radio.hasAttribute('data-other') || !radio.dataset.next) return;
      const step = this.stepFor(radio);
      clearTimeout(this.advanceTimer);
      this.advanceTimer = setTimeout(() => {
        if (radio.checked && this.stepMap.get(this.current) === step) this.go(radio.dataset.next);
      }, 260);
    }

    show(n, { focus = true } = {}) {
      const step = this.stepMap.get(String(n));
      if (!step) return;
      this.current = String(n);
      this.history = this.history.filter((s) => this.stepMap.has(s) && s !== this.current);
      this.steps.forEach((s) => s.classList.toggle('is-active', s === step));
      this.prepareFields();
      if (this.prevBtn) this.prevBtn.disabled = this.history.length === 0;
      if (this.nextBtn) this.nextBtn.disabled = step.hasAttribute('data-final');
      this.closeTips();
      this.updateProgress(step);

      const title = step.querySelector('.cdq-step__title');
      if (this.live && title) {
        const label = (this.config.stepOf || 'Step [n]').replace('[n]', String(this.history.length + 1));
        this.live.textContent = label + ': ' + title.textContent.trim();
      }
      if (focus) {
        title?.focus({ preventScroll: true });
        this.scrollToCard();
      }
      this.save();
    }

    scrollToCard() {
      const card = this.querySelector('.cdq-card');
      if (!card) return;
      const offset = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
      const top = card.getBoundingClientRect().top;
      if (top < offset || top > window.innerHeight * 0.45) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: top + window.scrollY - offset - 12, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    updateProgress(step) {
      const group = Number(step.dataset.group);
      const fraction = PROGRESS[step.dataset.step] || 0;
      this.progressItems.forEach((item, index) => {
        const g = index + 1;
        const p = g < group ? 1 : g === group ? fraction : 0;
        item.style.setProperty('--p', p * 100 + '%');
        item.classList.toggle('is-done', g < group);
        item.classList.toggle('is-current', g === group && p > 0);
        item.classList.toggle('has-cheer', g === group && p > 0.3);
      });
    }

    /* ---------- option state ---------- */

    syncGroup(radio) {
      const group = this.form.querySelectorAll('input[type="radio"][name="' + CSS.escape(radio.name) + '"]');
      group.forEach((r) => r.closest('.cdq-opt__label')?.classList.toggle('is-selected', r.checked));
    }

    syncOther(step) {
      const other = step?.querySelector('[data-cdq-other]');
      if (!other) return;
      const on = Boolean(step.querySelector('.cdq-opt__input[data-other]:checked'));
      const wasHidden = other.hidden;
      other.hidden = !on;
      const input = other.querySelector('input');
      if (input) input.disabled = !on || !this.activeSteps().has(step.dataset.step);
      const cont = step.querySelector('[data-cdq-other-continue]');
      if (cont) cont.hidden = !on;
      if (on && wasHidden && this.initialized && !this.restoring) other.querySelector('input')?.focus();
    }

    syncFeatures() {
      const hidden = this.querySelector('[data-cdq-features]');
      if (!hidden) return;
      hidden.value = Array.from(this.querySelectorAll('[data-cdq-feature]:checked')).map((el) => el.value).join(', ');
    }

    syncConsent() {
      const box = this.querySelector('[data-cdq-consent]');
      const value = this.querySelector('[data-cdq-consent-value]');
      if (box && value) value.value = box.checked ? 'Yes' : 'No';
    }

    clearChoice(step) {
      step?.querySelectorAll('.cdq-opt__input[type="radio"]').forEach((r) => {
        r.checked = false;
        r.closest('.cdq-opt__label')?.classList.remove('is-selected');
      });
    }

    closeTips() {
      this.querySelectorAll('[data-cdq-tip][aria-expanded="true"]').forEach((tip) => tip.setAttribute('aria-expanded', 'false'));
    }

    /* ---------- validation ---------- */

    errorFor(el) {
      const id = el.getAttribute('aria-describedby');
      return id ? document.getElementById(id) : el.closest('[data-cdq-drop]')?.querySelector('[data-cdq-error]');
    }

    setError(el, errorEl, message) {
      if (el) {
        el.classList.add('is-invalid');
        el.setAttribute('aria-invalid', 'true');
      }
      if (errorEl) {
        errorEl.textContent = message || '';
        errorEl.hidden = !message;
      }
    }

    clearStepErrors(step, onlyEl) {
      if (!step) return;
      const top = step.querySelector('.cdq-error--top');
      if (top && (!onlyEl || onlyEl.type === 'radio')) top.hidden = true;
      if (onlyEl && onlyEl.matches('.cdq-opt__input[type="radio"]')) {
        const otherInput = step.querySelector('[data-cdq-other] input');
        if (otherInput && !onlyEl.hasAttribute('data-other')) {
          otherInput.classList.remove('is-invalid');
          const err = this.errorFor(otherInput);
          if (err) err.hidden = true;
        }
      }
    }

    validate(step) {
      let firstInvalid = null;
      const fail = (el, errorEl, message) => {
        this.setError(el, errorEl, message);
        if (!firstInvalid) firstInvalid = el || errorEl;
      };

      if (step.hasAttribute('data-required-choice') && !step.querySelector('.cdq-opt__input[type="radio"]:checked')) {
        fail(null, step.querySelector('.cdq-error--top'), this.config.errChoice);
        firstInvalid = step.querySelector('.cdq-opt__input');
      }

      step.querySelectorAll('[data-cdq-number]').forEach((input) => {
        const value = input.value.trim();
        if (value && !NUMBER_RE.test(value)) fail(input, this.errorFor(input), this.config.errNumber);
      });

      const other = step.querySelector('.cdq-opt__input[data-other]:checked');
      if (other) {
        const text = step.querySelector('[data-cdq-other] input');
        if (text && !text.value.trim()) fail(text, this.errorFor(text), this.config.errOther);
      }

      if (step.hasAttribute('data-require-file')) {
        const drop = step.querySelector('[data-cdq-drop]');
        if (drop && !(this.files[drop.dataset.key] || []).length) {
          fail(null, drop.querySelector('[data-cdq-error]'), this.config.errFile);
          firstInvalid = drop.querySelector('[data-cdq-files]');
        }
      }

      step.querySelectorAll('[data-cdq-contact]').forEach((input) => {
        const value = input.value.trim();
        let ok = Boolean(value);
        if (ok && input.dataset.cdqContact === 'email') ok = EMAIL_RE.test(value);
        if (ok && input.dataset.cdqContact === 'phone') {
          const digits = value.replace(/\D/g, '').length;
          ok = digits >= 10 && digits <= 15 && /^[\d\s()+.-]+$/.test(value);
        }
        if (ok && input.dataset.cdqContact === 'zip') ok = ZIP_RE.test(value);
        if (!ok) fail(input, this.errorFor(input), input.dataset.error);
      });

      if (firstInvalid) {
        firstInvalid.focus?.();
        return false;
      }
      return true;
    }

    /* ---------- uploads ---------- */

    initUploads() {
      this.querySelectorAll('[data-cdq-drop]').forEach((drop) => {
        const key = drop.dataset.key;
        const input = drop.querySelector('[data-cdq-files]');
        const zone = drop.querySelector('.cdq-drop__zone');
        this.files[key] = [];
        input.addEventListener('change', () => {
          this.addFiles(drop, input.files);
          input.value = '';
        });
        ['dragenter', 'dragover'].forEach((type) =>
          zone.addEventListener(type, (event) => {
            event.preventDefault();
            drop.classList.add('is-over');
          })
        );
        ['dragleave', 'dragend'].forEach((type) => zone.addEventListener(type, () => drop.classList.remove('is-over')));
        zone.addEventListener('drop', (event) => {
          event.preventDefault();
          drop.classList.remove('is-over');
          if (event.dataTransfer?.files) this.addFiles(drop, event.dataTransfer.files);
        });
      });
    }

    addFiles(drop, list) {
      const key = drop.dataset.key;
      const maxBytes = (Number(this.config.maxFileMb) || 10) * 1024 * 1024;
      const maxFiles = Number(this.config.maxFiles) || 5;
      const error = drop.querySelector('[data-cdq-error]');
      let rejected = false;
      Array.from(list || []).forEach((file) => {
        const typeOk = FILE_RE.test(file.name) || /^(image\/(jpeg|png)|application\/pdf)$/.test(file.type);
        if (!typeOk || file.size > maxBytes || this.files[key].length >= maxFiles) {
          rejected = true;
          return;
        }
        this.files[key].push(file);
      });
      error.textContent = rejected ? this.config.errFileType || '' : '';
      error.hidden = !rejected;
      this.renderFiles(drop);
    }

    renderFiles(drop) {
      const list = drop.querySelector('[data-cdq-file-list]');
      list.textContent = '';
      this.files[drop.dataset.key].forEach((file, index) => {
        const li = document.createElement('li');
        li.className = 'cdq-file';
        const name = document.createElement('span');
        name.className = 'cdq-file__name';
        name.textContent = file.name;
        const size = document.createElement('span');
        size.className = 'cdq-file__size';
        size.textContent = file.size >= 1048576 ? (file.size / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(file.size / 1024)) + ' KB';
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'cdq-file__remove';
        remove.dataset.cdqFileRemove = '';
        remove.dataset.index = String(index);
        remove.setAttribute('aria-label', 'Remove ' + file.name);
        remove.textContent = '×';
        li.append(name, size, remove);
        list.append(li);
      });
    }

    /* ---------- dialogs ---------- */

    bindDialogs() {
      this.querySelectorAll('[data-cdq-dialog]').forEach((dialog) => {
        dialog.querySelector('[data-cdq-dialog-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          dialog.querySelector('iframe')?.removeAttribute('src');
          document.documentElement.classList.remove('cdq-lock');
          this.dialogOpener?.focus();
        });
      });
    }

    openDialog(opener) {
      const dialog = this.querySelector('[data-cdq-dialog="' + opener.dataset.cdqDialogOpen + '"]');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const iframe = dialog.querySelector('iframe');
      if (iframe?.dataset.src) iframe.src = iframe.dataset.src;
      this.dialogOpener = opener;
      document.documentElement.classList.add('cdq-lock');
      dialog.showModal();
    }

    /* ---------- submit ---------- */

    activeSteps() {
      return new Set([...this.history, this.current]);
    }

    prepareFields() {
      const active = this.activeSteps();
      this.steps.forEach((step) => {
        const on = active.has(step.dataset.step);
        step.querySelectorAll('input, textarea').forEach((el) => {
          if (el.type !== 'file') el.disabled = !on;
        });
        const otherInput = step.querySelector('[data-cdq-other] input');
        if (otherInput && !step.querySelector('.cdq-opt__input[data-other]:checked')) otherInput.disabled = true;
      });
      this.syncFeatures();
      this.syncConsent();
      const first = this.querySelector('[data-cdq-contact="first"]')?.value.trim() || '';
      const last = this.querySelector('[data-cdq-contact="last"]')?.value.trim() || '';
      const fullname = this.querySelector('[data-cdq-fullname]');
      if (fullname) fullname.value = (first + ' ' + last).trim();
    }

    resetBusy() {
      this.querySelector('[data-cdq-submit]')?.removeAttribute('aria-busy');
      this.submitting = false;
      this.allowSubmit = false;
    }

    async submit() {
      if (this.submitting) return;
      const step = this.stepMap.get('12');
      if (!step || !this.validate(step)) return;
      const button = this.querySelector('[data-cdq-submit]');
      const errorBox = this.querySelector('[data-cdq-submit-error]');
      if (errorBox) errorBox.hidden = true;
      this.submitting = true;
      button?.setAttribute('aria-busy', 'true');
      this.save(true);
      this.prepareFields();

      if (this.dataset.mode !== 'endpoint') {
        this.allowSubmit = true;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.submit();
        setTimeout(() => this.resetBusy(), 4000);
        return;
      }

      const data = new FormData();
      Array.from(this.form.elements).forEach((el) => {
        if (!el.name || el.disabled || el.type === 'file') return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        data.append(el.dataset.key || el.name, el.value);
      });
      const active = this.activeSteps();
      this.querySelectorAll('[data-cdq-drop]').forEach((drop) => {
        if (!active.has(this.stepFor(drop).dataset.step)) return;
        (this.files[drop.dataset.key] || []).forEach((file) => data.append(drop.dataset.key + '[]', file, file.name));
      });
      data.append('submitted_at', new Date().toISOString());

      try {
        await fetch(this.dataset.endpoint, { method: 'POST', body: data, mode: 'no-cors' });
        this.clearState();
        this.showThanks(true);
        this.afterSuccess();
      } catch (error) {
        this.resetBusy();
        if (errorBox) errorBox.hidden = false;
      }
    }

    showThanks(focus) {
      if (!this.thanks) return;
      if (this.form) this.form.hidden = true;
      this.thanks.hidden = false;
      if (this.form && this.thanks.closest('form') === this.form) {
        this.form.hidden = false;
        this.form.querySelectorAll('.cdq-stage, .cdq-progress').forEach((el) => { el.hidden = true; });
      }
      if (focus) {
        this.thanks.focus({ preventScroll: true });
        this.thanks.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }

    afterSuccess() {
      if (this.designMode) return;
      this.thanks?.focus({ preventScroll: true });
      const redirect = this.dataset.redirect;
      if (redirect) setTimeout(() => window.location.assign(redirect), 900);
    }

    /* ---------- saved progress ---------- */

    fieldKey(step, el) {
      const isChoice = el.type === 'radio' || el.type === 'checkbox';
      return step.dataset.step + ':' + (el.dataset.key || el.id || el.name) + (isChoice ? ':' + el.value : '');
    }

    save(now) {
      if (this.designMode || this.restoring || !this.steps) return;
      clearTimeout(this.saveTimer);
      const write = () => {
        const fields = {};
        this.steps.forEach((step) => {
          if (step.hasAttribute('data-final')) return;
          step.querySelectorAll('input, textarea').forEach((el) => {
            if (el.type === 'file' || el.type === 'hidden') return;
            const key = this.fieldKey(step, el);
            if (el.type === 'radio' || el.type === 'checkbox') {
              if (el.checked) fields[key] = true;
            } else if (el.value.trim()) {
              fields[key] = el.value;
            }
          });
        });
        try {
          localStorage.setItem(this.dataset.storage, JSON.stringify({ v: 1, t: Date.now(), current: this.current, history: this.history, fields }));
        } catch (error) {
          /* storage unavailable */
        }
      };
      if (now) write();
      else this.saveTimer = setTimeout(write, 250);
    }

    restore() {
      let data = null;
      try {
        data = JSON.parse(localStorage.getItem(this.dataset.storage) || 'null');
      } catch (error) {
        return null;
      }
      if (!data || data.v !== 1 || !data.fields || Date.now() - (data.t || 0) > STATE_TTL) return null;
      this.restoring = true;
      this.steps.forEach((step) => {
        step.querySelectorAll('input, textarea').forEach((el) => {
          if (el.type === 'file' || el.type === 'hidden') return;
          const value = data.fields[this.fieldKey(step, el)];
          if (el.type === 'radio' || el.type === 'checkbox') el.checked = value === true;
          else if (typeof value === 'string') el.value = value;
        });
        step.querySelectorAll('.cdq-opt__input').forEach((el) => el.closest('.cdq-opt__label')?.classList.toggle('is-selected', el.checked));
        this.syncOther(step);
      });
      this.syncFeatures();
      this.restoring = false;
      const history = Array.isArray(data.history) ? data.history.map(String).filter((s) => this.stepMap.has(s)) : [];
      const current = this.stepMap.has(String(data.current)) ? String(data.current) : '1';
      return { history: current === '1' ? [] : history, current };
    }

    clearState() {
      try {
        localStorage.removeItem(this.dataset.storage);
      } catch (error) {
        /* storage unavailable */
      }
    }
  }

  customElements.define('ceme-design-quiz', CemeDesignQuiz);
}
